import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { properties } from "../data/properties";
import FeaturedCarousel from "../components/FeaturedCarousel";
import { useHeroSlider } from "../hooks/useHeroSlider";
import { useIntersectionReveal } from "../hooks/useIntersectionReveal";
import { FEATURED_MAX, WA_URL } from "../config/constants";
import "./Home.css";

/** Hero background images — kept here as they are view-layer data, not business logic. */
const HERO_SLIDES_META = [
  {
    img:       "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=75&auto=format&fit=crop&fm=webp",
    imgMobile: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=70&auto=format&fit=crop&fm=webp",
    color:     "#1a1410",
  },
  {
    img:       "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=75&auto=format&fit=crop&fm=webp",
    imgMobile: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=70&auto=format&fit=crop&fm=webp",
    color:     "#101418",
  },
  {
    img:       "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1920&q=75&auto=format&fit=crop&fm=webp",
    imgMobile: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=70&auto=format&fit=crop&fm=webp",
    color:     "#0f1218",
  },
];

export default function Home() {
  const navigate    = useNavigate();
  const { t, i18n } = useTranslation("home");
  const { t: tc }   = useTranslation("common");

  // Domain hooks
  const { slide, prevSlide, handleGoTo } = useHeroSlider(HERO_SLIDES_META.length);
  useIntersectionReveal();

  // Local UI state (simple enough to stay inline)
  const [filter, setFilter] = useState({ type: "", emirate: "", priceType: "" });

  // Merge i18n text into slide meta
  const slidesI18n = t("slides", { returnObjects: true });
  const SLIDES = HERO_SLIDES_META.map((meta, i) => ({
    ...meta,
    tag:   slidesI18n[i].tag,
    title: [slidesI18n[i].title0, slidesI18n[i].title1],
    sub:   slidesI18n[i].sub,
  }));

  const featured  = properties.filter((p) => p.featured).slice(0, FEATURED_MAX);
  const services  = t("services.items", { returnObjects: true });
  const stats     = t("stats", { returnObjects: true });

  const handleSearch = () => {
    const params = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => v && params.set(k, v));
    navigate(`/listings?${params}`);
  };

  const handleFilterChange = (key) => (e) =>
    setFilter((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <main className="home">
      <Helmet>
        <title>ProNet Real Estate — Dubai Properties For Sale &amp; Rent</title>
        <meta name="description" content="Discover premium properties for sale and rent in Dubai. Browse exclusive apartments, villas, and commercial spaces with ProNet Real Estate — your trusted Dubai agency." />
        <link rel="canonical" href="https://pronet-realestate.com/" />
        <meta property="og:title" content="ProNet Real Estate — Dubai Properties For Sale &amp; Rent" />
        <meta property="og:description" content="Discover premium properties for sale and rent in Dubai. Browse exclusive apartments, villas, and commercial spaces with ProNet Real Estate." />
        <meta property="og:url" content="https://pronet-realestate.com/" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* ── HERO ── */}
      <section className="hero">
        {SLIDES.map((s, i) => (
          <div
            key={i}
            className={`hero-slide ${i === slide ? "hero-slide-active" : ""} ${i === prevSlide ? "hero-slide-prev" : ""}`}
            style={{ background: s.color }}
          >
            <img
              src={s.img}
              srcSet={`${s.imgMobile} 800w, ${s.img} 1920w`}
              sizes="100vw"
              alt={s.title[0]}
              className={i === slide ? "hero-img-active" : "hero-img"}
              fetchpriority={i === 0 ? "high" : "low"}
              loading={i === 0 ? "eager" : "lazy"}
            />
            <div className="hero-tint" />
          </div>
        ))}

        <div className="hero-content">
          <div className="hero-tag">
            <span className="hero-tag-line" />
            <span>{SLIDES[slide].tag}</span>
          </div>
          <h1 className={`hero-h1${i18n.language === "ru" ? " hero-h1--ru" : ""}`} key={slide}>
            <span className="hero-line1">{SLIDES[slide].title[0]}</span>
            <span className="hero-line2"><em>{SLIDES[slide].title[1]}</em></span>
          </h1>
          <p className="hero-sub" key={`sub-${slide}`}>{SLIDES[slide].sub}</p>
          <div className="hero-btns">
            <Link to="/listings" className="btn-gold"><span>{tc("btn.browseList")}</span></Link>
            <Link to="/contact"  className="btn-outline">{tc("btn.contactAgent")}</Link>
          </div>
        </div>

        <div className="hero-counter">
          <span className="hero-cur">0{slide + 1}</span>
          <span className="hero-sep" />
          <span className="hero-total">0{SLIDES.length}</span>
        </div>

        <div className="hero-dots">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              className={`hero-dot ${i === slide ? "active" : ""}`}
              onClick={() => handleGoTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>

        <div className="scroll-hint">
          <div className="scroll-line" />
          <span>{t("scroll")}</span>
        </div>
      </section>

      {/* ── SEARCH ── */}
      <div className="search-bar">
        <div className="search-field">
          <label htmlFor="search-type">{t("search.type")}</label>
          <select id="search-type" value={filter.type} onChange={handleFilterChange("type")}>
            <option value="">{t("search.typeAll")}</option>
            <option>Apartment</option><option>Villa</option><option>Studio</option><option>Commercial</option>
          </select>
        </div>
        <div className="search-field">
          <label htmlFor="search-location">{t("search.location")}</label>
          <select id="search-location" value={filter.emirate} onChange={handleFilterChange("emirate")}>
            <option value="">{t("search.locAll")}</option>
            <option>Dubai</option><option>Abu Dhabi</option><option>Sharjah</option>
          </select>
        </div>
        <div className="search-field">
          <label htmlFor="search-beds">{t("search.beds")}</label>
          <select id="search-beds"><option>Any</option><option>1</option><option>2</option><option>3</option><option>4+</option></select>
        </div>
        <div className="search-field no-border">
          <label htmlFor="search-status">{t("search.status")}</label>
          <select id="search-status" value={filter.priceType} onChange={handleFilterChange("priceType")}>
            <option value="">{t("search.statusAll")}</option>
            <option value="sale">{t("search.statusSale")}</option>
            <option value="rent">{t("search.statusRent")}</option>
          </select>
        </div>
        <button className="search-btn" onClick={handleSearch}>
          <span>{t("search.btn")}</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="4" stroke="currentColor" strokeWidth="1"/>
            <path d="M10 10L13 13" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      {/* ── STATS ── */}
      <div className="stats-row">
        {[
          ["24", "h",  stats.availability],
          ["100", "%", stats.support],
          ["98", "%",  stats.satisfaction],
          ["4", "",    stats.emirates],
        ].map(([n, s, l], i) => (
          <div key={i} className="stat reveal">
            <div className="stat-n">{n}<sup>{s}</sup></div>
            <div className="stat-l">{l}</div>
          </div>
        ))}
      </div>

      {/* ── FEATURED ── */}
      <section className="section">
        <div className="container">
          <div className="section-head reveal">
            <div className="section-eyebrow">
              <span className="eyebrow-line" /><span>{t("featured.eyebrow")}</span>
            </div>
            <h2 className="section-title">{t("featured.title")} <em>{t("featured.titleEm")}</em></h2>
            <Link to="/listings" className="section-more">{t("featured.viewAll")} <span>→</span></Link>
          </div>
          <FeaturedCarousel properties={featured} />
        </div>
      </section>

      {/* ── BIG FEATURE ── */}
      <section className="big-feature reveal">
        <div className="big-feature-img">
          <img src="/listings/la-alqueria/10.webp" alt="La Alqueria Villa" loading="lazy" />
        </div>
        <div className="big-feature-content">
          <div className="section-eyebrow"><span className="eyebrow-line" /><span>{t("bigFeature.eyebrow")}</span></div>
          <h2 className="big-feature-title">La Alqueria<br /><em>Benahavis</em></h2>
          <div className="big-feature-details">
            <div><span>9,000,000</span><small>EUR</small></div>
            <div><span>5</span><small>Bedrooms</small></div>
            <div><span>1,266</span><small>m²</small></div>
          </div>
          <p>{t("bigFeature.desc")}</p>
          <Link to="/international/la-alqueria-benahavis-villa" className="btn-gold"><span>{t("bigFeature.viewProp")}</span></Link>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section className="services-sec">
        <div className="container">
          <div className="section-head reveal">
            <div className="section-eyebrow"><span className="eyebrow-line" /><span>{t("services.eyebrow")}</span></div>
            <h2 className="section-title">{t("services.title")} <em>{t("services.titleEm")}</em></h2>
          </div>
          <div className="services-grid">
            {services.map((svc, i) => (
              <div key={i} className="service-item reveal">
                <div className="service-num">0{i + 1}</div>
                <div className="service-line" />
                <h3 className="service-title">{svc.title}</h3>
                <p className="service-desc">{svc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PARTNERS ── */}
      <section className="partners-sec reveal">
        <div className="container">
          <p className="partners-label">{t("partners.label")}</p>
          <div className="partners-grid">
            {[
              { src: "/developers/emaar.png",   alt: "Emaar",   scale: 0.8 },
              { src: "/developers/meraas.png",  alt: "Meraas" },
              { src: "/developers/damac.png",   alt: "Damac" },
              { src: "/developers/nakheel.png", alt: "Nakheel" },
              { src: "/developers/omniyat.png", alt: "Omniyat" },
              { src: "/developers/imtiaz.png",  alt: "Imtiaz" },
            ].map(({ src, alt, scale }) => (
              <div key={alt} className="partner-logo">
                <img src={src} alt={alt} style={scale ? { transform: `scale(${scale})` } : {}} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-sec reveal">
        <div className="cta-bg">
          <img src="https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=1600&q=85" alt="Dubai" />
          <div className="cta-overlay" />
        </div>
        <div className="cta-content">
          <h2 className="cta-title">{t("cta.title0")}<br /><em>{t("cta.title1")}</em></h2>
          <p>{t("cta.sub")}</p>
          <div className="cta-btns">
            <a href={WA_URL} target="_blank" rel="noreferrer" className="btn-gold"><span>{t("cta.whatsapp")}</span></a>
            <Link to="/contact" className="btn-outline">{t("cta.send")}</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
